import React from "react";
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
  Step,
  StepLabel,
  Stepper,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
} from "@mui/material";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import LoginIcon from "@mui/icons-material/Login";
import SearchIcon from "@mui/icons-material/Search";
import InfoIcon from "@mui/icons-material/Info";
import KeyIcon from "@mui/icons-material/Key";
import LinkIcon from "@mui/icons-material/Link";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import BookmarkAddedIcon from "@mui/icons-material/BookmarkAdded";
import BookIcon from "@mui/icons-material/Book";
import AssignmentIcon from "@mui/icons-material/Assignment";

const steps = [
  "Obtener credenciales",
  "Acceder a la biblioteca",
  "Buscar recursos",
  "Consultar detalles",
];

const BibliotecaVirtualPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Container maxWidth="lg" sx={{ py: 5 }}>
      <Card elevation={3}>
        <CardContent>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            mb={3}
          >
            <LibraryBooksIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography
              variant="h4"
              fontWeight="bold"
              color="primary"
            >
              Biblioteca Virtual FCEFA
            </Typography>
          </Stack>

          <Typography
            variant="h6"
            gutterBottom
            color="text.secondary"
          >
            Guía paso a paso para acceder y utilizar los recursos
            digitales
          </Typography>

          <Divider sx={{ my: 3 }} />

          {/* Stepper */}
          <Stepper
            activeStep={-1}
            orientation={isMobile ? "vertical" : "horizontal"}
            sx={{ mb: 4 }}
          >
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontSize: isMobile ? "0.8rem" : "1rem" }}
                  >
                    {label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Paso 1: Obtener credenciales */}
          <Paper
            elevation={1}
            sx={{ p: 3, mb: 3, backgroundColor: "#f8f9fa" }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              mb={2}
            >
              <KeyIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Paso 1: Obtener tus credenciales de acceso
              </Typography>
            </Stack>

            <Typography variant="body1" paragraph>
              Antes de ingresar a la biblioteca virtual, necesitas
              obtener tu nombre de usuario y contraseña.
            </Typography>

            <Typography
              variant="subtitle1"
              fontWeight="medium"
              gutterBottom
            >
              Instrucciones:
            </Typography>

            <List dense>
              <ListItem>
                <ListItemIcon>
                  <LinkIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography>
                      Accede al portal de credenciales:
                      <Typography
                        component="span"
                        fontWeight="bold"
                        color="primary"
                        sx={{ ml: 1 }}
                      >
                        <Link
                          href="http://claves.fcefa.edu.bo/"
                          targe
                          color="inherit"
                          underline="hover"
                          target="_blank"
                        >
                          🔑 CLAVES Y CONTRASEÑAS FCEFA
                        </Link>
                      </Typography>
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <InfoIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography>
                      Completa los datos solicitados:
                    </Typography>
                  }
                  secondary={
                    <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                      <li>
                        <strong>Carnet de Identidad:</strong> Ingresa
                        tu número de carnet completo
                      </li>
                      <li>
                        <strong>Fecha de Nacimiento:</strong> Utiliza
                        el formato DD/MM/AAAA
                      </li>
                    </Box>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography>
                      Genera tus accesos y haz clic en el botón
                      correspondiente
                    </Typography>
                  }
                  secondary="Guarda en un lugar seguro tu usuario y contraseña para futuros accesos"
                />
              </ListItem>
            </List>
          </Paper>

          {/* Paso 2: Acceder a la biblioteca */}
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              mb={2}
            >
              <LoginIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Paso 2: Acceder a la biblioteca virtual
              </Typography>
            </Stack>

            <List dense>
              <ListItem>
                <ListItemIcon>
                  <LinkIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography>
                      Ingresa al sitio web:
                      <Typography
                        component="span"
                        fontWeight="bold"
                        color="primary"
                        sx={{ ml: 1 }}
                      >
                        <Link
                          href="https://biblioteca.fcefa.edu.bo/"
                          targe
                          color="inherit"
                          underline="hover"
                          target="_blank"
                        >
                            📚 BIBLIOTECA VIRTUAL FCEFA
                        </Link>
                      </Typography>
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <LoginIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography>
                      Haz clic en la opción{" "}
                      <strong>"Iniciar Sesión"</strong>
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <KeyIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography>
                      Ingresa tu <strong>usuario</strong> y{" "}
                      <strong>contraseña</strong> obtenidos en el Paso
                      1
                    </Typography>
                  }
                />
              </ListItem>
            </List>
          </Paper>

          {/* Paso 3: Buscar recursos */}
          <Paper
            elevation={1}
            sx={{ p: 3, mb: 3, backgroundColor: "#f8f9fa" }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              mb={2}
            >
              <SearchIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Paso 3: Buscar recursos
              </Typography>
            </Stack>

            <Typography variant="body1" paragraph>
              Una vez dentro de la biblioteca virtual, puedes buscar
              los materiales que necesitas:
            </Typography>

            <Typography
              variant="subtitle1"
              fontWeight="medium"
              gutterBottom
            >
              Métodos de búsqueda:
            </Typography>

            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <li>
                <BookIcon
                  sx={{
                    fontSize: 16,
                    mr: 1,
                    verticalAlign: "middle",
                  }}
                />{" "}
                Por título
              </li>
              <li>
                <AssignmentIcon
                  sx={{
                    fontSize: 16,
                    mr: 1,
                    verticalAlign: "middle",
                  }}
                />{" "}
                Por autor
              </li>
              <li>
                <SearchIcon
                  sx={{
                    fontSize: 16,
                    mr: 1,
                    verticalAlign: "middle",
                  }}
                />{" "}
                Por tema
              </li>
              <li>
                <InfoIcon
                  sx={{
                    fontSize: 16,
                    mr: 1,
                    verticalAlign: "middle",
                  }}
                />{" "}
                Por ISBN
              </li>
              <li>
                <LibraryBooksIcon
                  sx={{
                    fontSize: 16,
                    mr: 1,
                    verticalAlign: "middle",
                  }}
                />{" "}
                Por palabras clave
              </li>
            </Box>

            <Typography
              variant="subtitle1"
              fontWeight="medium"
              gutterBottom
            >
              Cómo realizar la búsqueda:
            </Typography>

            <List dense>
              <ListItem>
                <ListItemIcon>
                  <SearchIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography>
                      Escribe tu criterio de búsqueda en la barra
                      correspondiente
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography>
                      Haz clic en el <strong>ícono de la lupa</strong>{" "}
                      🔍 o presiona "Enter"
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <InfoIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography>
                      Revisa los resultados que aparecen en pantalla
                    </Typography>
                  }
                />
              </ListItem>
            </List>
          </Paper>

          {/* Paso 4: Consultar detalles */}
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              mb={2}
            >
              <InfoIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Paso 4: Consultar detalles y opciones
              </Typography>
            </Stack>

            <List dense>
              <ListItem>
                <ListItemIcon>
                  <BookIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography>
                      Haz clic en el <strong>título</strong> del libro
                      o material
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <InfoIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography>
                      Consulta la información detallada:
                    </Typography>
                  }
                  secondary={
                    <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                      <li>Datos bibliográficos completos</li>
                      <li>
                        <strong>Código de clasificación</strong>{" "}
                        (Ejemplo: Clasificación CDD: Eco 310 C18)
                      </li>
                      <li>
                        <strong>Ubicación física</strong> en la
                        biblioteca
                      </li>
                      <li>Disponibilidad del material</li>
                    </Box>
                  }
                />
              </ListItem>
            </List>

            <Typography
              variant="subtitle1"
              fontWeight="medium"
              gutterBottom
              sx={{ mt: 2 }}
            >
              Opciones adicionales:
            </Typography>

            <Box component="ul" sx={{ pl: 3 }}>
              <li>
                <CheckCircleIcon
                  sx={{
                    fontSize: 16,
                    mr: 1,
                    verticalAlign: "middle",
                  }}
                />{" "}
                <strong>Reservar el libro:</strong> Si el material no
                está disponible, puedes realizar una reserva online
              </li>
              <li>
                <BookmarkIcon
                  sx={{
                    fontSize: 16,
                    mr: 1,
                    verticalAlign: "middle",
                  }}
                />{" "}
                <strong>Guardar en favoritos:</strong> Para consultas
                futuras
              </li>
              <li>
                <BookmarkAddedIcon
                  sx={{
                    fontSize: 16,
                    mr: 1,
                    verticalAlign: "middle",
                  }}
                />{" "}
                <strong>Exportar citación:</strong> Para tus trabajos
                académicos
              </li>
            </Box>
          </Paper>

          {/* Consejos útiles */}
          <Paper
            elevation={1}
            sx={{
              p: 3,
              backgroundColor: "#e3f2fd",
              borderLeft: "4px solid #1976d2",
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              gutterBottom
              color="primary"
            >
              💡 Consejos útiles:
            </Typography>

            <Box component="ul" sx={{ pl: 3 }}>
              <li>
                <strong>Mantén tus credenciales seguras</strong> y no
                las compartas con otros usuarios
              </li>
              <li>
                <strong>Cierra sesión</strong> al terminar tu
                búsqueda, especialmente si usas computadoras
                compartidas
              </li>
              <li>
                <strong>Verifica los horarios</strong> de atención de
                la biblioteca física para retirar los materiales
                reservados
              </li>
              <li>
                <strong>Renueva tus libros</strong> a tiempo para
                evitar recargos
              </li>
            </Box>

            <Typography
              variant="body1"
              sx={{ mt: 2, fontStyle: "italic" }}
            >
              ¿Tienes problemas para acceder? Contacta al personal de
              la biblioteca para recibir ayuda personalizada.
            </Typography>
          </Paper>
        </CardContent>
      </Card>
    </Container>
  );
};

export default BibliotecaVirtualPage;
